<template>
  <LibraryListItem :class="{ 'employee-acknowledgement-inactive': isInactive }">
    <div class="select-col" @click.stop>
      <DpplDarkTooltip
        v-if="checkboxDisabled && checkboxDisabledReason"
        tooltip-placement="right"
        light-mode
      >
        <template #activator="{ onMouseenter, onMouseleave }">
          <div
            class="select-col-activator"
            @mouseenter="onMouseenter"
            @mouseleave="onMouseleave"
          >
            <DpplSimpleCheckbox
              :model-value="selected"
              :option="{ id: ulid, label: '' }"
              hide-label
              size="md"
              border-radius="6px"
              :disabled="checkboxDisabled"
              @update:model-value="$emit('update:selected', $event)"
            />
          </div>
        </template>
        <template #tooltip-content>
          <p class="text-body-2 text-textDark">
            {{ checkboxDisabledReason }}
          </p>
        </template>
      </DpplDarkTooltip>
      <DpplSimpleCheckbox
        v-else
        :model-value="selected"
        :option="{ id: ulid, label: '' }"
        hide-label
        size="md"
        border-radius="6px"
        @update:model-value="$emit('update:selected', $event)"
      />
    </div>

    <div class="employee-acknowledgement-col">
      <div class="name text-body-2 ellipsis-col">
        {{ firstName }} {{ lastName }}
      </div>
      <div v-if="email" class="email text-body-3 ellipsis-col">
        {{ email }}
      </div>
    </div>

    <div class="text-body-2 job-profile-col">
      <div
        v-if="jobProfileUlid && jobProfileTitle"
        class="job-profile-title-row"
      >
        <span
          class="job-profile-title-text"
          :title="jobProfileTitle"
        >{{ jobProfileTitle }}</span>
        <span class="job-profile-open-wrap">
          <DpplDarkTooltip
            tooltip-placement="left"
            :tooltip-offset="12"
            light-mode
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <router-link
                :to="{ name: 'job-profile-hris-view', params: { id: jobProfileUlid } }"
                target="_blank"
                rel="noopener noreferrer"
                class="job-profile-link d-flex align-center"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
                @click.stop="handleViewJobProfileClick"
              >
                <IconOpen class="job-profile-open-icon" />
              </router-link>
            </template>
            <template #tooltip-content>
              <p class="text-body-2 text-textDark">
                View Job Profile
              </p>
            </template>
          </DpplDarkTooltip>
        </span>
      </div>
      <div
        v-else-if="jobProfileUlid"
        class="job-profile-title-row"
      >
        <span class="job-profile-title-text">View Job Profile</span>
        <span class="job-profile-open-wrap">
          <router-link
            :to="{ name: 'job-profile-hris-view', params: { id: jobProfileUlid } }"
            target="_blank"
            rel="noopener noreferrer"
            class="job-profile-link d-flex align-center"
            @click.stop="handleViewJobProfileClick"
          >
            <IconOpen class="job-profile-open-icon" />
          </router-link>
        </span>
      </div>
      <template v-else>
        <span class="job-profile-fallback-title">{{ jobProfileTitle || '' }}</span>
      </template>
    </div>

    <div class="text-body-2 acknowledgement-status-col">
      <StatusPill
        :status-key="acknowledgementStatusKey"
        :label="acknowledgementStatus"
      />
      <div v-if="showPendingSlaWarning" class="d-flex align-center ml-1">
        <InsightsTooltip
          :tooltip="pendingSlaWarningTooltip"
          icon="$alert-circle-outline"
          icon-color="error"
          tooltip-direction="end"
        />
      </div>
    </div>

    <div class="text-body-2 days-since-col">
      {{ typeof daysSinceLastAcknowledgement === 'number' ? daysSinceLastAcknowledgement : '' }}
    </div>

    <div class="actions-col" @click.stop>
      <template v-if="actionVariant === 'pending'">
        <div class="action-buttons-row">
          <div
            v-if="pendingNotificationFailed && emailFailedAlertTooltip?.html"
            class="action-alert-icon d-flex align-center"
          >
            <InsightsTooltip
              :tooltip="emailFailedAlertTooltip"
              icon-color="error"
            />
          </div>
          <HideRbac :permissions="['sendJobProfileAcknowledgementReminders']">
            <DpplNewButton
              v-if="canSendReminder"
              variant="outlined"
              height="32"
              class="action-remind-btn"
              @click="$emit('send-reminder', employeeAcknowledgementRow)"
            >
              Remind
            </DpplNewButton>
          </HideRbac>
        </div>
      </template>
      <div v-else-if="actionVariant === 'create'" class="action-create-wrap">
        <HideRbac :permissions="['createEmployeeJobProfileAcknowledgements']">
          <DpplNewButton
            variant="outlined"
            color="primary"
            height="32"
            class="action-create-btn"
            @click="$emit('create', employeeAcknowledgementRow)"
          >
            Create
          </DpplNewButton>
        </HideRbac>
      </div>
    </div>
  </LibraryListItem>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import LibraryListItem from '@/components/shared/LibraryListItem.vue';
import StatusPill from '@/components/shared/StatusPill.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import DpplSimpleCheckbox from '@/components/datapeople/DpplSimpleCheckbox.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import { getTooltip } from '@/helpers/tooltips';
import { useAcknowledgementsConfigStore } from '@/stores/jobProfiles/acknowledgementsConfig';
import { useTracking } from '@/stores/tracking/tracking';
import IconOpen from '@/assets/icon_open.svg?component';

const props = defineProps({
  firstName: { type: String, default: '' },
  lastName: { type: String, default: '' },
  email: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  jobProfileTitle: { type: String, default: '' },
  jobProfileUlid: { type: String, default: '' },
  acknowledgementStatusKey: { type: String, default: null },
  acknowledgementStatus: { type: String, default: '' },
  daysPending: { type: Number, default: null },
  daysSinceLastAcknowledgement: { type: [Number, null], default: null },
  isInactive: { type: Boolean, default: false },
  ulid: { type: String, required: true },
  employeeAcknowledgementRow: { type: Object, default: null },
  actionVariant: {
    type: String,
    default: 'none',
    validator: (v) => ['none', 'pending', 'cancel-only', 'create'].includes(v),
  },
  checkboxDisabled: { type: Boolean, default: false },
  checkboxDisabledReason: { type: String, default: '' },
  pendingNotificationFailed: { type: Boolean, default: false },
});

defineEmits([
  'update:selected',
  'send-reminder',
  'create',
]);

const emailFailedAlertTooltip = computed(() => getTooltip('acknowledgement-email-failed-alert'));

const { acknowledgementPendingSla } = storeToRefs(useAcknowledgementsConfigStore());

const showPendingSlaWarning = computed(() => (
  props.acknowledgementStatusKey === 'pending_acknowledgement'
  && typeof props.daysPending === 'number'
  && Number(acknowledgementPendingSla.value) > 0
  && props.daysPending >= Number(acknowledgementPendingSla.value)
));

const pendingSlaWarningTooltip = computed(() => ({
  key: 'acknowledgement-pending-sla-warning',
  html: `Acknowledgement request pending for ${props.daysPending} days`,
}));

const canSendReminder = computed(() => Boolean(
  props.email
    && (props.jobProfileUlid || props.jobProfileTitle),
));

const handleViewJobProfileClick = () => {
  useTracking().ackLibClickedViewJobProfile();
};
</script>

<style lang="less" scoped>
:deep(.document-list-item) {
  gap: 0;
}

.select-col {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-left: 16px;
}

.select-col-activator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.employee-acknowledgement-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.ellipsis-col {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 16px;
}

.overflow {
  padding-left: 16px;
}

.job-profile-col {
  max-width: 100%;
  padding-left: 16px;
}

.job-profile-title-row {
  display: flex;
  align-items: center;
  column-gap: 6px;
}

.job-profile-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-profile-fallback-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

:deep(.job-profile-link) {
  font-size: 13px;
  color: @text-brand;

  &:visited {
    color: @text-brand;
  }
}

.job-profile-open-icon {
  width: 13px;
  height: 13px;
}

.acknowledgement-status-col {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 16px;
  justify-self: start;
  min-width: 0;
}

.days-since-col {
  text-align: center;
  padding-left: 0;
  justify-self: center;
}

.name {
  color: @text-darkest;
}

.email {
  color: @text-light;
}

.actions-col {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0;
  justify-self: center;
  min-width: 0;
  width: 100%;
}

.action-alert-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.action-buttons-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.action-create-wrap {
  display: flex;
  justify-content: center;
}

:deep(.action-create-btn.v-btn):not(.v-btn--disabled):hover {
  background-color: @bg-brand-light;
}

:deep(.action-remind-btn.v-btn):not(.v-btn--disabled):hover {
  background-color: @bg-dark;
}

.muted {
  color: @text-light;
}

// Inactive employee acknowledgement row styling - gray out all text and disable interactions
:deep(.employee-acknowledgement-inactive) {
  opacity: 0.6;
  pointer-events: none;
  cursor: default;

  .select-col {
    pointer-events: auto;
  }

  .name,
  .email,
  .text-body-2,
  .text-body-3 {
    color: @text-light;
  }

  .view-col {
    .view-acknowledgements-btn {
      opacity: 0.6;
      pointer-events: none;
    }
  }
}
</style>
